import { CreateReferalData } from '../../api/createReferal';
import { AssessmentSummaryEntity } from '../../api/fetchAssessmentSummary';
import { ComplimentaryDrugResponse } from '../../api/fetchComplimentaryDrugs';
import { DoctorPackages } from '../../api/fetchDoctorPackages';
import { Drug, LabTest } from '../../api/fetchDrugsLabTestsFromSymDia';
import { SubstitutesResponse } from '../../api/fetchDrugsSubstitute';
import { PastVitalsData } from '../../api/fetchPastVitalsByPtOid';
import { Response } from '../../api/fetchPxDraft';
import { Res, SpecialityEntity } from '../../api/fetchSpecialityForDoctor';
import { RelatedSymDia } from '../../api/fetchSymDiaFromSymDia';
import { PropertyGroupEntity } from '../../api/fetchSymDiaProperties';
import { PadDForm, PrintConfig, Translate } from '../../api/pushVisit';
import { ChartType } from '../../components/prescriptionPad/leftPanel/DentalChart/DentalChart';
import { ProcessedDentalExaminations } from '../../components/prescriptionPad/leftPanel/DentalChart/DentalExamination';
import { Inventory } from '../../components/prescriptionPad/rightPanel/workspace/PrescribeAutoSuggestV2';
import { filterPresentingSymptoms } from '../../pages/prescriptionPad/AssessmentSummaryRightPanel';
import { getDrugAllergy } from '../../pages/utils';
import { GlobalState } from '../reducers';
import {
    AST,
    DrugInteractionException,
    PatientDetailsMR,
    PatientDetailsVisits,
    RelatedProfileData,
    ToastType,
} from '../reducers/prescriptionPad';
import { MHC, MHCWithMilestone, PatientMedicalCondition } from '../types/medicalHistory';
import {
    AdvicesEntity,
    DiagnosisEntityOrSymptomsEntity,
    Followup,
    LabTestsEntityOrProceduresEntity,
    MedicationsEntity,
    ExaminationEntity,
    Prescription,
    VitalsEntity,
    PatientPersonalProfile,
    Notes,
    LabVitalEntity,
    Attachment,
    PadElements,
    DentalExamination,
    DentalProcedure,
    Patient,
    InjectionsEntity,
    IpdAdmission,
    Doctor,
} from '../types/prescription';
import { uniqueBy } from '../utils';
import { PatientHealthSummaryWithPoolingResponse } from '../../api/getPatientHealthSummaryWithPooling';
import { IMilestoneHistory } from '../../components/common/types/milestones';
import { TreatmentPlan } from '../../api/getTreatmentPlans';
import { CareCanvas } from '../types/CareCanvas';

export type LivePreviewComplaint = {
    title: string;
    details: { [key: string]: string };
};

export const getPrescription = (state: GlobalState): Prescription | undefined => {
    return state.prescriptionPad.prescription;
};

export const getFollowUp = (state: GlobalState): Followup | undefined => {
    return state.prescriptionPad.prescription?.followup;
};

export const getAdvices = (state: GlobalState): AdvicesEntity[] => {
    return [...(state.prescriptionPad.prescription?.advices || [])];
};

export const getVitals = (state: GlobalState): VitalsEntity[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    return prescription.vitals || [];
};

export const getMilestones = (state: GlobalState): IMilestoneHistory[] => {
    const prescription = state.prescriptionPad.prescription;

    if (!prescription) {
        return [];
    }

    return prescription.allMilestones?.length
        ? prescription.allMilestones
        : (prescription.patientHistory as MHCWithMilestone)?.milestones ?? [];
};

export const getInventory = (state: GlobalState): Inventory => {
    return state.prescriptionPad.inventory || {};
};

export const getPatientMedicalConditions = (state: GlobalState): PatientMedicalCondition[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    return prescription.patientHistory?.patientMedicalConditions || [];
};

export const getPatientPersonalProfile = (
    state: GlobalState,
): PatientPersonalProfile | undefined => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return;
    }
    return prescription.patient.profile.personal;
};

export const getPatientMedicalHistory = (state: GlobalState): MHC | undefined => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return;
    }
    return prescription.patientHistory;
};

export const isMedicalHistoryFilled = (state: GlobalState): boolean => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return false;
    }

    const medicalHistory = prescription.patientHistory;

    if (!medicalHistory) {
        return false;
    }

    return isMHFilled(medicalHistory);
};

export const getSymptoms = (state: GlobalState): DiagnosisEntityOrSymptomsEntity[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    return prescription.symptoms || [];
};

export const getElementsFromPrescription = <T>(k: keyof Prescription) => (
    state: GlobalState,
): T[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    return (prescription[k] || []) as T[];
};

export const getLabVitals = (state: GlobalState): LabVitalEntity[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    return prescription.labVitals || [];
};

export const isCreateDialogVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.isUseTemplateDialogVisible || false;
};

export const getRefer = (state: GlobalState): CreateReferalData | undefined => {
    return state.prescriptionPad.prescription?.refer;
};

export const getPtPastVitals = (state: GlobalState): PastVitalsData | undefined => {
    return state.prescriptionPad.ptPastVitals;
};

export const isPPFilled = (state: GlobalState): boolean => {
    const isFilled =
        getSymptoms(state).length > 0 ||
        getDiagnosis(state).length > 0 ||
        getMedications(state).length > 0 ||
        getLabTests(state).length > 0 ||
        getLabVitals(state).length > 0;

    return isFilled;
};

export const getDiagnosis = (state: GlobalState): DiagnosisEntityOrSymptomsEntity[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    return prescription.diagnosis || [];
};

export const getMedications = (state: GlobalState): MedicationsEntity[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    return prescription.medications || [];
};

export const getInjections = (state: GlobalState): InjectionsEntity[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    return prescription.injections || [];
};

export const getLabTests = (state: GlobalState): LabTestsEntityOrProceduresEntity[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    return prescription.labTests || [];
};

// TODO
export const getExaminations = (state: GlobalState): ExaminationEntity[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    return prescription.examinations || [];
};

export const getSuggestedDrugs = (state: GlobalState): Drug[] => {
    const suggestedDrugs = state.prescriptionPad.suggestedDrugs;
    if (!suggestedDrugs) {
        return [];
    }
    return suggestedDrugs || [];
};

export const getSuggestedLabTests = (state: GlobalState): LabTest[] => {
    const suggestedLabTests = state.prescriptionPad.suggestedLabTests;
    if (!suggestedLabTests) {
        return [];
    }
    return suggestedLabTests || [];
};

export const getRelatedSymptoms = (state: GlobalState): RelatedSymDia[] => {
    return state.prescriptionPad.relatedSymptons || [];
};

export const getRelatedDiagnosis = (state: GlobalState): LabTest[] => {
    return state.prescriptionPad.relatedDiagnosis || [];
};

export const isExaminationsDialogVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.exa;
};

export const isVitalsDialogVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.vitals;
};
export const isReferalDialogVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.referal;
};

export const isSummaryDialogVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.summary;
};

export const getIsPastVitalsDialogVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.longitudinalVitalView;
};

export const getIsABDMBannerVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.abdmBanner;
};

export const getIsHealthWeekVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.healthWeek;
};

export const getIsSubstitutionVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.substitutions;
};

export const isLabVitalDialogVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.labVital;
};

export const isPhDialogVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.ph;
};

export const isPPNotesDialogVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.ppNotes;
};

export const getPrescriptionDyForm = (state: GlobalState): PadDForm[] => {
    return state.prescriptionPad.prescription?.dyform || [];
};

export const isPPNotesFilled = (state: GlobalState): boolean => {
    return (
        (
            (state.prescriptionPad.prescription?.prescriptionNotes?.text || '') +
            (state.prescriptionPad.prescription?.privateNotes?.text || '')
        ).length > 0
    );
};

export const getPrescriptionNotes = (state: GlobalState): Notes | undefined => {
    return state.prescriptionPad.prescription?.prescriptionNotes;
};

export const getTranslate = (state: GlobalState): Translate => {
    return state.prescriptionPad.translate || { flag: false, lang: '' };
};

export const getPrivateNotes = (state: GlobalState): Notes | undefined => {
    return state.prescriptionPad.prescription?.privateNotes;
};

export const getDrugAllergies = (state: GlobalState): Set<string> | undefined => {
    const drugAllergy = getPatientMedicalHistory(state)?.drugAllergy?.filter(
        (item) => item.status?.toLowerCase() !== 'inactive',
    );
    const set: Set<string> = new Set();
    drugAllergy?.forEach((item) => {
        set.add(item.id);
        set.add(item.name.toLowerCase());
        item.generic_id && set.add(item.generic_id);
        item.generic_name && set.add(item.generic_name?.toLowerCase());
    });
    return set;
};

export const getDrugInteractions = (state: GlobalState): DrugInteractionException[] => {
    const medications = getMedications(state);
    const allergies = getDrugAllergies(state);
    if (!allergies?.size) {
        return state.prescriptionPad.drugInteractions || [];
    }
    const results = [...(state.prescriptionPad.drugInteractions || [])];

    medications.forEach((med) => {
        if (getDrugAllergy(allergies, med)) {
            results.push({
                type: 'allergy',
                drugs: [{ id: med.id, name: med.name, generic: med.generic_name || '-' }],
            });
        }
    });
    return results;
};

export const getComplimentaryDrug = (state: GlobalState): ComplimentaryDrugResponse => {
    return state.prescriptionPad.complimentaryDrugs || [];
};

export const getSymFromSa = (state: GlobalState): AssessmentSummaryEntity[] => {
    const wholeSa = getAssessmentSummary(state);
    if (!wholeSa) {
        return [];
    }
    const present: AssessmentSummaryEntity[] = filterPresentingSymptoms(wholeSa);
    return uniqueBy<AssessmentSummaryEntity>('mdbid', present);
};

export const getAssessmentSummary = (state: GlobalState): AST | undefined => {
    return state.prescriptionPad.assessmentSummary;
};

export const getLabsAvailableToRefer = (state: GlobalState): SpecialityEntity[] => {
    return state.prescriptionPad.labsAvailableToRefer?.lab || [];
};
export const getAvailableToReferData = (state: GlobalState): Res | undefined => {
    return state.prescriptionPad.labsAvailableToRefer;
};
export const getSelectedLabToReferId = (state: GlobalState): string | undefined => {
    return state.prescriptionPad.selectedLabToReferId;
};

export const getToastType = (state: GlobalState): ToastType => {
    return state.prescriptionPad.toastVisible;
};

export const getDraft = (state: GlobalState): Response | undefined => {
    return state.prescriptionPad.draft;
};

export const getPrintConfigs = (state: GlobalState): PrintConfig | undefined => {
    return state.prescriptionPad.printConfigs;
};

export const getMedicalHistoryCardState = (state: GlobalState): boolean => {
    return state.prescriptionPad?.medicalHistoryCardState ? true : false;
};

export const getNoKnownMedicalHistoryState = (state: GlobalState): boolean => {
    return state?.prescriptionPad?.noKnownMedicalHistory ? true : false;
};

export const isAssessmentSummaryFilled = (state: GlobalState): boolean => {
    return (
        (state.prescriptionPad.assessmentSummary?.fetchState === 'success' &&
            ((state.prescriptionPad.assessmentSummary?.sa &&
                state.prescriptionPad.assessmentSummary.sa.sy &&
                state.prescriptionPad.assessmentSummary.sa.sy.length > 0) ||
                (state.prescriptionPad.assessmentSummary?.sa &&
                    state.prescriptionPad.assessmentSummary.sa.riskScore &&
                    state.prescriptionPad.assessmentSummary.sa.riskScore.length > 0) ||
                (state.prescriptionPad.assessmentSummary?.sa &&
                    state.prescriptionPad.assessmentSummary.sa.dy &&
                    state.prescriptionPad.assessmentSummary.sa.dy.length > 0) ||
                (state.prescriptionPad.assessmentSummary?.sa &&
                    state.prescriptionPad.assessmentSummary.sa.mh &&
                    state.prescriptionPad.assessmentSummary.sa.mh.length > 0) ||
                (state.prescriptionPad.assessmentSummary?.sa &&
                    state.prescriptionPad.assessmentSummary.sa.notes &&
                    state.prescriptionPad.assessmentSummary.sa.notes.length > 0) ||
                (state.prescriptionPad.assessmentSummary?.media || [])?.length > 0 ||
                (state.prescriptionPad.assessmentSummary?.qna &&
                    state.prescriptionPad.assessmentSummary.qna?.length > 0) ||
                (state.prescriptionPad.assessmentSummary?.vitals &&
                    Object.keys(state.prescriptionPad.assessmentSummary?.vitals).length > 0))) ||
        false
    );
};

export const getAllSymDiaProperties = (
    state: GlobalState,
): { [k: string]: PropertyGroupEntity[] } => {
    return state.prescriptionPad.symDiaProperties || {};
};

export const getSymDiaProperties = (state: GlobalState, id: string): PropertyGroupEntity[] => {
    return (
        (state.prescriptionPad.symDiaProperties && state.prescriptionPad.symDiaProperties[id]) || []
    );
};

export const isPrescriptionEmpty = (prescriptionElements: PadElements): boolean => {
    const pres = prescriptionElements;

    const isPrescriptionEmpty =
        (!pres.vitals || pres.vitals.length === 0) &&
        (!pres.symptoms || pres.symptoms.length === 0) &&
        (!pres.diagnosis || pres.diagnosis.length === 0) &&
        (!pres.medicalHistory || !isMHFilled(pres.medicalHistory)) &&
        (!pres.medications || pres.medications.length === 0) &&
        (!pres.labTests || pres.labTests.length === 0) &&
        (!pres.examinations || pres.examinations.length === 0) &&
        (!pres.followup || !pres.followup?.date) &&
        !pres.prescriptionNotes &&
        (!pres.advices || pres.advices.length === 0) &&
        (!pres.dyform || pres.dyform.length === 0) &&
        (!pres.labVitals || pres.labVitals.length === 0) &&
        !pres.refer &&
        (!pres.dentalExaminations || pres.dentalExaminations.length === 0) &&
        (!pres.dentalProcedures || pres.dentalProcedures.length === 0) &&
        (!pres.opVision || pres.opVision.length === 0) &&
        (!pres.opIop || pres.opIop.length === 0) &&
        (!pres.opCurrentSpec || pres.opCurrentSpec.length === 0) &&
        (!pres.opSubjectiveRefraction || pres.opSubjectiveRefraction.length === 0) &&
        (!pres.opAutoRefraction || pres.opAutoRefraction.length === 0) &&
        (!pres.opFinalPrescription || pres.opFinalPrescription.length === 0) &&
        (!pres.injections || pres.injections.length === 0) &&
        (!pres.procedures || pres.procedures.length === 0) &&
        (!pres.careCanvas || pres.careCanvas.length === 0);

    return isPrescriptionEmpty;
};

export const isMHFilled = (medicalHistory: MHC): boolean => {
    const {
        patientMedicalConditions,
        familyHistory,
        lifestyleHabits,
        recentTravelHistory,
        currentMedications,
        drugAllergy,
        foodOtherAllergy,
        pastProcedures,
        otherMedicalHistory,
    } = medicalHistory;

    return (
        (patientMedicalConditions && patientMedicalConditions.length > 0) ||
        (familyHistory && familyHistory.length > 0) ||
        (lifestyleHabits && lifestyleHabits.length > 0) ||
        (recentTravelHistory && recentTravelHistory.length > 0) ||
        (currentMedications && currentMedications.length > 0) ||
        (drugAllergy && drugAllergy.length > 0) ||
        (foodOtherAllergy && foodOtherAllergy.length > 0) ||
        (pastProcedures && pastProcedures.length > 0) ||
        (otherMedicalHistory && otherMedicalHistory.length > 0) ||
        false
    );
};

export const getPatientDetailsVisits = (state: GlobalState): PatientDetailsVisits | undefined => {
    return state.prescriptionPad.patientDetails?.visits;
};

export const getPatientDetailsMR = (state: GlobalState): PatientDetailsMR | undefined => {
    return state.prescriptionPad.patientDetails?.mr;
};

export const getAllPtIdsPatient = (state: GlobalState): string[] | undefined => {
    return state.prescriptionPad.patientDetails?.allPtIds;
};

export const magicBoxActiveTab = (state: GlobalState): string | undefined =>
    state.prescriptionPad.magicBoxActiveTab;

export const getAttachment = (state: GlobalState): Attachment | undefined =>
    state.prescriptionPad.prescription?.attachment;

export const getDrugsSubstitute = (state: GlobalState): SubstitutesResponse => {
    return state.prescriptionPad.drugsSubstitute || [];
};

export const getDoctorPackages = (state: GlobalState): DoctorPackages => {
    return state.prescriptionPad.doctorPackages || [];
};

export const getRxClinicId = (state: GlobalState): string | undefined => {
    return state.prescriptionPad.prescription?.clinic._id;
};

export const getRxDoctor = (state: GlobalState): Doctor | undefined => {
    return state.prescriptionPad.prescription?.doctor;
};

export const getDentalExaminations = (state: GlobalState): DentalExamination[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    const { dentalExaminations } = prescription;
    return dentalExaminations?.map((i, index) => ({ ...i, index })) || [];
};

export const getDentalProcedures = (state: GlobalState): DentalProcedure[] => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return [];
    }
    const { dentalProcedures } = prescription;
    return dentalProcedures?.map((i, index) => ({ ...i, index })) || [];
};

export const getChartType = (state: GlobalState): ChartType => {
    return state.prescriptionPad.chartType || '';
};

export const getProcessedDentalExaminations = (
    state: GlobalState,
): ProcessedDentalExaminations[] => {
    return state.prescriptionPad.processedDentalExaminations || [];
};

export const getPatient = (state: GlobalState): Patient | undefined => {
    const prescription = state.prescriptionPad.prescription;
    if (!prescription) {
        return;
    }
    return prescription.patient;
};

export const getPatientHealthSummary = (
    state: GlobalState,
): PatientHealthSummaryWithPoolingResponse | undefined => {
    const prescriptionPad = state.prescriptionPad;
    if (!prescriptionPad) {
        return;
    }
    return prescriptionPad.patientHealthSummary;
};

export const getIsFetchingPatientHealthSummary = (state: GlobalState): boolean => {
    const prescriptionPad = state.prescriptionPad;
    if (!prescriptionPad) {
        return true;
    }
    return prescriptionPad.isFetchingPatientHealthSummary;
};

export const getIsSupportTicketsVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.supportTickets;
};

export const getIsTroubleshootMySystemVisible = (state: GlobalState): boolean => {
    return state.prescriptionPad.diaglogs.troubleshootMySystem;
};

export const getInjectionElements = (state: GlobalState): InjectionsEntity[] => {
    return state.prescriptionPad.prescription?.injections || [];
};

export const getOngoingTreatmentPlans = (state: GlobalState): TreatmentPlan[] => {
    return state.prescriptionPad.ongoingTreatmentPlans || [];
};

export const getIpdAdmission = (state: GlobalState): IpdAdmission | undefined => {
    return state.prescriptionPad.prescription?.ipdAdmission;
};

export const getCareCanvas = (state: GlobalState): CareCanvas[] | undefined => {
    return state.prescriptionPad.prescription?.careCanvas;
};

export const getRelatedProfilesData = (
    state: GlobalState,
): { [k: string]: RelatedProfileData } | undefined => {
    return state.prescriptionPad.relatedProfilesData;
};
